import { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  List, 
  ListItemButton, 
  ListItemText, 
  TextField, 
  InputAdornment, 
  IconButton, 
  useMediaQuery, 
  useTheme, 
  Paper, 
  AppBar, 
  Toolbar, 
  Drawer, 
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Alert,
  Badge,
  Tooltip
} from '@mui/material';
import {
  Search,
  ChevronRight,
  ArrowLeft,
  Menu,
  Mail,
  RefreshCw,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Info
} from 'lucide-react';
import FormRenderer from './FormRenderer';

// Use REACT_APP_API_URL_DEVELOPMENT if available, otherwise fallback to REACT_APP_API_URL
import { API_URL } from '../../utils/apiConfig';

// Reset form function
const resetForm = async (employeeId, formId) => {
  const token = localStorage.getItem('token');
  try {
    const response = await fetch(`${API_URL}/forms/reset`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ employeeId, formId })
    });

    if (!response.ok) {
      throw new Error('Failed to reset form');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error resetting form:', error);
    throw error;
  }
};
// Update this function in your ManagerDocumentReview.js component
const sendReminderEmail = async (employeeId, pendingForms = []) => {
  const token = localStorage.getItem('token');
  try {
    // Changed from /employees/send-form-reminder to /api/send-form-reminder
    // to match your backend endpoint
    const response = await fetch(`${API_URL}/send-form-reminder`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ 
        employeeId, 
        pendingForms 
      })
    });

    if (!response.ok) {
      throw new Error('Failed to send reminder email');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error sending reminder email:', error);
    throw error;
  }
};

// Also update the getEmailLogs function in the same file
const getEmailLogs = async (employeeId) => {
  const token = localStorage.getItem('token');
  try {
    // Changed from /employees/${employeeId}/email-logs to /api/${employeeId}/email-logs
    // to match your backend endpoint
    const response = await fetch(`${API_URL}/${employeeId}/email-logs`, {
      headers: { 
        Authorization: `Bearer ${token}` 
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch email logs');
    }

    const data = await response.json();
    return data.logs || [];
  } catch (error) {
    console.error('Error fetching email logs:', error);
    return [];
  }
};
// Status indicator dot
const StatusDot = ({ status, isCompleted }) => {
  const getColor = () => {
    if (isCompleted) return '#34C759'; // Green for completed forms
    if (status === 'Pending') return '#FF9500'; // Orange for pending forms
    return '#8E8E93'; // Gray for others
  };

  return (
    <Box
      sx={{
        width: 8,
        height: 8,
        borderRadius: '50%',
        backgroundColor: getColor(),
        marginRight: 1,
      }}
    />
  );
};

// Progress indicator component
const ProgressIndicator = ({ completed, total }) => (
  <Typography
    component="span"
    sx={{ fontSize: '12px', color: '#8E8E93', ml: 1 }}
  >
    {completed}/{total} Forms
  </Typography>
);

// Email Logs Dialog Component
const EmailLogsDialog = ({ open, onClose, logs }) => (
  <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
    <DialogTitle>Email Communication History</DialogTitle>
    <DialogContent>
      {logs.length === 0 ? (
        <DialogContentText>No email logs found for this employee.</DialogContentText>
      ) : (
        <List>
          {logs.map((log, index) => (
            <Box key={index} sx={{ mb: 2, p: 2, borderLeft: '4px solid #1976d2', bgcolor: 'background.paper' }}>
              <Typography variant="subtitle1" fontWeight="bold">
                {log.type === 'automated' ? 'System Notification' : 'Manual Reminder'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {new Date(log.sentAt).toLocaleString()}
              </Typography>
              <Typography variant="body1">{log.message}</Typography>
              {log.pendingForms && log.pendingForms.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" fontWeight="bold">Forms mentioned:</Typography>
                  <Box component="ul" sx={{ mt: 0.5, pl: 2 }}>
                    {log.pendingForms.map(form => (
                      <Typography component="li" key={form} variant="body2">
                        {form}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          ))}
        </List>
      )}
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Close</Button>
    </DialogActions>
  </Dialog>
);

// Reminder Email Dialog
const ReminderDialog = ({ open, onClose, employee, pendingForms, onSend }) => {
  const [sending, setSending] = useState(false);
  
  const handleSend = async () => {
    if (!employee) return;
    
    setSending(true);
    try {
      await onSend();
      onClose();
    } catch (error) {
      console.error("Failed to send reminder:", error);
    } finally {
      setSending(false);
    }
  };
  
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Send Reminder Email</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Send a reminder email to {employee?.firstName} {employee?.lastName} ({employee?.email}) 
          about their incomplete forms.
        </DialogContentText>
        
        {pendingForms?.length > 0 ? (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2">The following forms need to be completed:</Typography>
            <List dense>
              {pendingForms.map((form, index) => (
                <Typography key={index} component="li">
                  • {form.formName}
                </Typography>
              ))}
            </List>
          </Box>
        ) : (
          <Typography sx={{ mt: 2 }} color="warning.main">
            No pending forms found.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          onClick={handleSend}
          disabled={sending || pendingForms?.length === 0}
          variant="contained"
        >
          {sending ? "Sending..." : "Send Reminder"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default function ManagerDocumentReview() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // States
  const [searchTerm, setSearchTerm] = useState('');
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [view, setView] = useState('employees'); // 'employees', 'documents', 'form'
  const [DocumentData, setDocumentData] = useState(null);
  const [user, setUser] = useState(null);
  
  // New states for enhancements
  const [reminderDialogOpen, setReminderDialogOpen] = useState(false);
  const [emailLogsDialogOpen, setEmailLogsDialogOpen] = useState(false);
  const [emailLogs, setEmailLogs] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [pendingForms, setPendingForms] = useState([]);

  useEffect(() => {
    const userToken = localStorage.getItem('token');
    if (userToken) {
      // Decode the JWT token to get user data
      const decodedToken = JSON.parse(atob(userToken.split('.')[1]));
      setUser(decodedToken);
    }
  }, []);

  // Fetch locations on mount
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const token = localStorage.getItem('token');
        const userStore = localStorage.getItem('userStore');
        const userData = JSON.parse(localStorage.getItem('user') || '{}');
        const isManager = userData.role === 'manager';

        console.log('Fetching locations with context:', {
          userStore,
          userRole: userData.role
        });

        const response = await fetch(`${API_URL}/locations`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await response.json();

        // If manager, filter to only show their store
        if (isManager && userStore) {
          const filteredLocations = data.filter(loc => String(loc.id) === String(userStore));
          setLocations(filteredLocations);
          setSelectedLocation(userStore); // Auto-select manager's store
        } else {
          setLocations(data || []);
        }
      } catch (error) {
        console.error('Error fetching locations:', error);
      }
    };
    fetchLocations();
  }, []);

  // Fetch all employees on component mount
  useEffect(() => {
    const fetchAllEmployees = async () => {
      try {
        const token = localStorage.getItem('token');
        const userData = JSON.parse(localStorage.getItem('user') || '{}');
        const userStore = localStorage.getItem('userStore');
        const isManager = userData.role === 'manager';

        // For managers, always use their store ID
        let url;
        if (isManager) {
          url = `${API_URL}/locations/${userStore}/employees`;
        } else {
          // For admins, fetch all employees
          url = `${API_URL}/employees`;
        }
        
        console.log('Fetching all employees:', {
          url,
          userRole: userData.role,
          userStore
        });

        const response = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` },
        });
        
        const data = await response.json();
        console.log('Fetched all employees:', data);

        setEmployees(data || []);
      } catch (error) {
        console.error('Error fetching all employees:', error);
        setEmployees([]);
      }
    };

    fetchAllEmployees();
  }, []);

  // Filter employees when location changes
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const token = localStorage.getItem('token');
        const userData = JSON.parse(localStorage.getItem('user') || '{}');
        const userStore = localStorage.getItem('userStore');
        const isManager = userData.role === 'manager';
  
        // If no location is selected or it's "All Locations", we already have all employees
        if (!selectedLocation && userData.role === 'admin') {
          return;
        }
        
        // For managers, always use their store ID
        const locationId = isManager ? userStore : selectedLocation;
        
        // Fetch employees for the specific location
        const url = `${API_URL}/locations/${locationId}/employees`;
        
        console.log('Fetching employees for location:', {
          url,
          locationId
        });
  
        const response = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` },
        });
        
        const data = await response.json();
        console.log('Fetched employees for location:', data);
  
        setEmployees(data || []);
        setSelectedEmployee(null);
        setDocuments([]);
        setSelectedDocument(null);
        setDocumentData(null);
  
      } catch (error) {
        console.error('Error fetching employees for location:', error);
        setEmployees([]);
      }
    };
  
    // Only fetch if we have a selected location (not "All Locations")
    if (selectedLocation) {
      fetchEmployees();
    }
  }, [selectedLocation]);

  // Handle document selection
  const handleSelectDocument = async (doc) => {
    setSelectedDocument(doc);
    setDocumentData(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/forms/get-form-data/${doc.formId}/${selectedEmployee.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await response.json();
      if (data.success) {
        setDocumentData(data);
      }
    } catch (error) {
      console.error('Error fetching document data:', error);
      setDocumentData(null);
    }
  };

  // Handle employee selection
  const handleSelectEmployee = async (emp) => {
    setSelectedEmployee(emp);
    setSelectedDocument(null);
    setDocumentData(null);
    setDocuments([]);
    setPendingForms([]);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/employees/${emp.id}/documents`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();

      if (data.success && Array.isArray(data.documents)) {
        setDocuments(data.documents);
        
        // Filter pending forms
        const pending = data.documents.filter(doc => !doc.isCompleted);
        setPendingForms(pending);
      } else {
        setDocuments([]);
        setPendingForms([]);
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
      setDocuments([]);
      setPendingForms([]);
    }
  };

  // Handle form submission
  const handleFormSubmit = async (formData) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/forms/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          formId: selectedDocument.formId,
          formData,
          employeeId: selectedEmployee.id,
        }),
      });

      if (response.ok) {
        showSnackbar('Form updated successfully', 'success');
        // Fetch updated document data
        const updatedData = await fetch(
          `${API_URL}/forms/get-form-data/${selectedDocument.formId}/${selectedEmployee.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const data = await updatedData.json();
        if (data.success) {
          setDocumentData(data);
        }
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      showSnackbar('Failed to update form', 'error');
    }
  };

  // Handle sending reminder email
  const handleSendReminder = async () => {
    if (!selectedEmployee) return;
    
    try {
      const formNames = pendingForms.map(form => form.formName);
      const result = await sendReminderEmail(selectedEmployee.id, formNames);
      
      showSnackbar('Reminder email sent successfully', 'success');
      // Refresh email logs
      fetchEmailLogs();
      
      return result;
    } catch (error) {
      console.error('Failed to send reminder:', error);
      showSnackbar('Failed to send reminder email', 'error');
      throw error;
    }
  };

  // Fetch email logs
  const fetchEmailLogs = async () => {
    if (!selectedEmployee) return;
    
    try {
      const logs = await getEmailLogs(selectedEmployee.id);
      setEmailLogs(logs);
    } catch (error) {
      console.error('Failed to fetch email logs:', error);
      setEmailLogs([]);
    }
  };

  // Open email logs dialog
  const handleOpenEmailLogs = async () => {
    if (!selectedEmployee) return;
    
    await fetchEmailLogs();
    setEmailLogsDialogOpen(true);
  };

  // Show snackbar helper
  const showSnackbar = (message, severity = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  // Filter employees by search term
  const filteredEmployees = employees.filter((emp) => {
    const fullName = `${emp.firstName} ${emp.lastName}`;
    return fullName.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const handleEmployeeSelect = async (emp) => {
    await handleSelectEmployee(emp);
    if (isMobile) {
      setView('documents');
    }
  };

  const handleDocumentSelect = async (doc) => {
    await handleSelectDocument(doc);
    if (isMobile) {
      setView('form');
    }
  };

  const handleBack = () => {
    if (view === 'form') {
      setView('documents');
      setSelectedDocument(null);
    } else if (view === 'documents') {
      setView('employees');
      setSelectedEmployee(null);
    }
  };

  const renderMobileHeader = () => (
    <AppBar position="static" color="default" elevation={1}>
      <Toolbar>
        {view !== 'employees' && (
          <IconButton edge="start" onClick={handleBack}>
            <ArrowLeft size={24} />
          </IconButton>
        )}
        <Typography variant="h6" sx={{ ml: 2, flex: 1 }}>
          {view === 'employees' && 'Employees'}
          {view === 'documents' && `${selectedEmployee?.firstName} ${selectedEmployee?.lastName}`}
          {view === 'form' && selectedDocument?.formName}
        </Typography>
        
        {view === 'documents' && selectedEmployee && (
          <>
            <IconButton 
              onClick={() => setReminderDialogOpen(true)} 
              color="primary"
              disabled={pendingForms.length === 0}
              sx={{ mr: 1 }}
            >
              <Mail size={24} />
            </IconButton>
            <IconButton 
              onClick={handleOpenEmailLogs} 
              color="primary"
            >
              <Clock size={24} />
            </IconButton>
          </>
        )}
      </Toolbar>
    </AppBar>
  );

  const renderEmployeeList = () => (
    <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
      {filteredEmployees.map((emp) => (
        <ListItemButton
          key={emp.id}
          onClick={() => handleEmployeeSelect(emp)}
          sx={{
            borderBottom: '1px solid #E5E5EA',
            py: 2,
          }}
        >
          <ListItemText
            primary={`${emp.firstName} ${emp.lastName}`}
            secondary={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography sx={{ fontSize: '12px' }}>
                  {emp.position}
                </Typography>
                <ProgressIndicator
                  completed={emp.completedForms}
                  total={emp.totalForms}
                />
              </Box>
            }
          />
          {emp.totalForms > 0 && emp.completedForms < emp.totalForms && (
            <Tooltip title="Has incomplete forms">
              <AlertCircle size={16} color="#FF9500" style={{ marginRight: 8 }} />
            </Tooltip>
          )}
          <ChevronRight size={20} />
        </ListItemButton>
      ))}
    </List>
  );

  const renderDocumentList = () => (
    <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
      {documents.map((doc) => (
        <ListItemButton
          key={doc.formId}
          onClick={() => handleDocumentSelect(doc)}
          sx={{
            borderBottom: '1px solid #E5E5EA',
            py: 2,
          }}
        >
          <StatusDot status={doc.documentStatus} isCompleted={doc.isCompleted} />
          <ListItemText
            primary={doc.formName}
            secondary={doc.documentStatus}
          />
          <ChevronRight size={20} />
        </ListItemButton>
      ))}
    </List>
  );

  const renderFormView = () => (
    <Box sx={{ p: 2 }}>
      {selectedDocument && (
        <>
          <FormRenderer
            key={`${selectedDocument.formId}-${selectedEmployee?.id}`}
            activeForm={selectedDocument.formId}
            employeeId={selectedEmployee?.id}
            onFormSubmit={async (formId) => {
              try {
                await handleFormSubmit(formId);
                await handleSelectEmployee(selectedEmployee);
              } catch (error) {
                console.error('Error submitting form:', error);
              }
            }}
          />
          
          {/* Reset form button for admin/manager */}
          {user && (user.role === 'admin' || user.role === 'manager') && (
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
              <Button
                variant="outlined"
                color="error"
                onClick={async () => {
                  if (window.confirm('Are you sure you want to reset this form? This will clear all submitted data.')) {
                    try {
                      await resetForm(selectedEmployee.id, selectedDocument.formId);
                      // Refresh the documents list
                      await handleSelectEmployee(selectedEmployee);
                      // Show success message
                      showSnackbar('Form has been reset successfully', 'success');
                    } catch (error) {
                      console.error('Error resetting form:', error);
                      showSnackbar('Failed to reset form. Please try again.', 'error');
                    }
                  }
                }}
                startIcon={<RefreshCw size={16} />}
              >
                Reset Form
              </Button>
            </Box>
          )}
        </>
      )}
    </Box>
  );

  // Mobile Layout
  // Update the mobile layout section in ManagerDocumentReview.js component

// Mobile Layout
if (isMobile) {
  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {renderMobileHeader()}
      
      <Box sx={{ p: 2, borderBottom: '1px solid #E5E5EA', display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Add location dropdown for mobile admin users */}
        {user && user.role === 'admin' && view === 'employees' && (
          <TextField
            select
            size="small"
            value={selectedLocation}
            onChange={(e) => setSelectedLocation(e.target.value)}
            fullWidth
            label="Location"
            SelectProps={{ native: true }}
          >
            <option value="">All Locations</option>
            {locations.map((location) => (
              <option key={location.id} value={location.id}>
                {location.name}
              </option>
            ))}
          </TextField>
        )}
        
        <TextField
          fullWidth
          size="small"
          placeholder="Search employees..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={20} />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {view === 'employees' && renderEmployeeList()}
        {view === 'documents' && renderDocumentList()}
        {view === 'form' && renderFormView()}
      </Box>
      
      {/* Dialogs */}
      <ReminderDialog
        open={reminderDialogOpen}
        onClose={() => setReminderDialogOpen(false)}
        employee={selectedEmployee}
        pendingForms={pendingForms}
        onSend={handleSendReminder}
      />
      
      <EmailLogsDialog
        open={emailLogsDialogOpen}
        onClose={() => setEmailLogsDialogOpen(false)}
        logs={emailLogs}
      />
      
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}

  // Desktop layout
  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Header Controls */}
      <Box
        sx={{
          p: 1,
          borderBottom: '1px solid #E5E5EA',
          bgcolor: 'white',
          display: 'flex',
          gap: 2,
        }}
      >
        {/* Location dropdown */}
        <TextField
          select
          size="small"
          value={selectedLocation}
          onChange={(e) => setSelectedLocation(e.target.value)}
          sx={{ minWidth: 200 }}
          SelectProps={{ native: true }}
        >
          <option value="">All Locations</option>
          {locations.map((location) => (
            <option key={location.id} value={location.id}>
              {location.name}
            </option>
          ))}
        </TextField>

        {/* Search employees */}
        <TextField
          fullWidth
          size="small"
          placeholder="Search employees..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={16} />
              </InputAdornment>
            ),
            sx: { fontSize: '13px' },
          }}
        />
      </Box>

      {/* Main content: 3 columns */}
      <Box sx={{ display: 'flex', flex: 1, bgcolor: 'white' }}>
        {/* Left Column: Employee List */}
        <Box
          sx={{
            width: 220,
            borderRight: '1px solid #E5E5EA',
            height: 'calc(100vh - 64px)', // Subtract header height
            overflow: 'auto',
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: '#f1f1f1',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#888',
              borderRadius: '4px',
              '&:hover': {
                backgroundColor: '#666',
              },
            },
          }}
        >
          <List disablePadding>
            {filteredEmployees.map((emp) => (
              <ListItemButton
                key={emp.id}
                selected={selectedEmployee?.id === emp.id}
                onClick={() => handleSelectEmployee(emp)}
                sx={{
                  borderBottom: '1px solid #E5E5EA',
                  py: 0.75,
                  minHeight: 36,
                }}
              >
                <ListItemText
                  primary={`${emp.firstName} ${emp.lastName}`}
                  secondary={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography sx={{ fontSize: '12px' }}>
                        {emp.position}
                      </Typography>
                      <ProgressIndicator
                        completed={emp.completedForms}
                        total={emp.totalForms}
                      />
                    </Box>
                  }
                  primaryTypographyProps={{
                    fontSize: '13px',
                    fontWeight: selectedEmployee?.id === emp.id ? 600 : 400,
                  }}
                />
                {emp.totalForms > 0 && emp.completedForms < emp.totalForms && (
                  <Tooltip title="Has incomplete forms">
                    <AlertCircle size={16} color="#FF9500" style={{ marginRight: 8 }} />
                  </Tooltip>
                )}
                <ChevronRight size={14} />
              </ListItemButton>
            ))}
          </List>
        </Box>

        {/* Middle Column: Documents & Email Actions */}
        {selectedEmployee && (
          <Box
            sx={{
              width: 220,
              borderRight: '1px solid #E5E5EA',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Email Actions Toolbar */}
            <Box sx={{ 
              p: 1.5, 
              borderBottom: '1px solid #E5E5EA', 
              display: 'flex',
              justifyContent: 'space-between'
            }}>
              <Tooltip title="Send reminder email">
                <IconButton 
                  color="primary" 
                  onClick={() => setReminderDialogOpen(true)}
                  disabled={pendingForms.length === 0}
                >
                  <Mail size={20} />
                </IconButton>
              </Tooltip>
              
              <Tooltip title="View email history">
                <IconButton 
                  color="primary" 
                  onClick={handleOpenEmailLogs}
                >
                  <Clock size={20} />
                </IconButton>
              </Tooltip>
            </Box>
            
            {/* Document List */}
            <Box sx={{ overflow: 'auto', flex: 1 }}>
              <List disablePadding>
                {documents.map((doc) => (
                  <ListItemButton
                    key={doc.formId}
                    selected={selectedDocument?.formId === doc.formId}
                    onClick={() => handleSelectDocument(doc)}
                    sx={{
                      borderBottom: '1px solid #E5E5EA',
                      py: 0.75,
                      minHeight: 36,
                    }}
                  >
                    <StatusDot status={doc.documentStatus} isCompleted={doc.isCompleted} />
                    <ListItemText
                      primary={doc.formName}
                      primaryTypographyProps={{
                        fontSize: '13px',
                        fontWeight: selectedDocument?.formId === doc.formId ? 600 : 400,
                      }}
                    />
                  </ListItemButton>
                ))}
              </List>
            </Box>
          </Box>
        )}

        {/* Right Column: Document Viewer */}
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {!selectedDocument ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                color: 'text.secondary',
              }}
            >
              Select a document to view
            </Box>
          ) : (
            <Paper
              variant="outlined"
              sx={{
                p: 3,
                borderRadius: 1,
                boxShadow: 1,
                width: '100%',
                minHeight: '600px',
              }}
            >
              <Typography variant="h5" gutterBottom sx={{ textAlign: 'center', fontWeight: 'bold' }}>
                {selectedDocument.formName}
              </Typography>
              <FormRenderer
                key={`${selectedDocument.formId}-${selectedEmployee?.id}`}
                activeForm={selectedDocument.formId}
                employeeId={selectedEmployee?.id}
                onFormSubmit={async (formId) => {
                  try {
                    await handleFormSubmit(formId);
                    // Refresh the documents list to update status
                    await handleSelectEmployee(selectedEmployee);
                  } catch (error) {
                    console.error('Error submitting form:', error);
                  }
                }}/>

                {/* Reset form button for admin/manager */}
                {user && (user.role === 'admin' || user.role === 'manager') && (
                  <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={async () => {
                        if (window.confirm('Are you sure you want to reset this form? This will clear all submitted data.')) {
                          try {
                            await resetForm(selectedEmployee.id, selectedDocument.formId);
                            // Refresh the documents list
                            await handleSelectEmployee(selectedEmployee);
                            // Show success message
                            showSnackbar('Form has been reset successfully', 'success');
                          } catch (error) {
                            console.error('Error resetting form:', error);
                            showSnackbar('Failed to reset form. Please try again.', 'error');
                          }
                        }
                      }}
                      startIcon={<RefreshCw size={16} />}
                    >
                      Reset Form
                    </Button>
                  </Box>
                )}
              </Paper>
            )}
          </Box>
        </Box>
        
        {/* Dialogs and Snackbars */}
        <ReminderDialog
          open={reminderDialogOpen}
          onClose={() => setReminderDialogOpen(false)}
          employee={selectedEmployee}
          pendingForms={pendingForms}
          onSend={handleSendReminder}
        />
        
        <EmailLogsDialog
          open={emailLogsDialogOpen}
          onClose={() => setEmailLogsDialogOpen(false)}
          logs={emailLogs}
        />
        
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert 
            onClose={() => setSnackbarOpen(false)} 
            severity={snackbarSeverity}
            sx={{ width: '100%' }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Box>
    );
  }
